import { subClientsApi } from "#/api/subClientsApi";
import Pagination from "#/components/buttons/Pagination";
import BaseTable from "#/components/tables/BaseTable";
import { EmptyState } from "#/components/ui/EmptyState";
import { useClient } from "#/hook/useClient";
import { formatDate, formatTime } from "#/utils/dateTimeUtils";
import { sanitizeItemStatusLabel } from "#/utils/StringUtils";
import { Accordion, Anchor, Loader, Table } from "@mantine/core";
import { IconCategory } from "@tabler/icons-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  subClientId: number;
}

const CARD_COLORS = ["blue", "teal", "orange", "grape", "pink", "indigo", "cyan", "lime"];

const AnalyticsTab = ({ subClientId }: Props) => {
  const { t } = useTranslation();
  const { client } = useClient();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);

  const { data: analytics, isPending: analyticsPending } = useQuery({
    queryKey: ["sub-client-analytics", subClientId],
    queryFn: async () => {
      const res = await subClientsApi.getAnalytics(subClientId);
      return res.data;
    },
    staleTime: 1000 * 30,
  });

  const {
    data: { items = [], total = 0, per_page = 0 } = {},
    isPending: historyPending,
  } = useQuery({
    queryKey: ["sub-client-scan-history", subClientId, page, limit],
    queryFn: async () => {
      const res = await subClientsApi.getScanHistory(subClientId, {
        page,
        limit,
        sortBy: "createdAt",
        order: "DESC",
      });
      return {
        items: res.data.data,
        total: res.data.total,
        per_page: res.data.limit,
      };
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 30,
  });

  const headers = [
    t("subClients.history.scannedAt"),
    t("subClients.history.item"),
    t("subClients.history.category"),
    t("subClients.history.status"),
  ];

  return (
    <div className="w-full grid grid-cols-1 lg:grid-cols-[7fr_3fr] gap-4 items-start">
      <div className="w-full flex flex-col gap-2 order-2 lg:order-1">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold">
            {t("subClients.analytics.scanHistory")}
          </div>
          <Pagination
            page={page}
            total={total}
            limit={per_page}
            onChange={setPage}
            onLimitChange={(l) => {
              setLimit(l);
              setPage(1);
            }}
          />
        </div>
        <BaseTable
          headers={headers}
          loading={historyPending}
          allowSorting={false}
          rows={items.map((row) => (
            <Table.Tr key={row.id}>
              <Table.Td style={{ minWidth: "180px" }}>
                <div className="flex flex-col">
                  <span>{formatDate(row.createdAt, client)}</span>
                  <span className="text-xs text-gray-500">
                    {formatTime(row.createdAt, client)}
                  </span>
                </div>
              </Table.Td>
              <Table.Td style={{ minWidth: "120px" }}>
                {row.item?.id ? (
                  <Anchor
                    size="sm"
                    underline="always"
                    onClick={() =>
                      window.open(`/inventory/items/${row.item?.id}`, "_blank")
                    }
                    className="cursor-pointer"
                  >
                    #{row.item.id}
                  </Anchor>
                ) : (
                  "--"
                )}
              </Table.Td>
              <Table.Td style={{ minWidth: "180px" }}>
                {row.item?.product?.id ? (
                  <Anchor
                    size="sm"
                    underline="always"
                    onClick={() =>
                      window.open(
                        `/inventory/categories/${row.item?.product?.id}`,
                        "_blank",
                      )
                    }
                    className="cursor-pointer"
                  >
                    {row.item.product.name ?? `#${row.item.product.id}`}
                  </Anchor>
                ) : (
                  "--"
                )}
              </Table.Td>
              <Table.Td style={{ minWidth: "150px" }}>
                {row.status_label ? sanitizeItemStatusLabel(row.status_label) : "--"}
              </Table.Td>
            </Table.Tr>
          ))}
        />
      </div>

      <div className="w-full order-1 lg:order-2">
        <Accordion variant="separated" defaultValue={["categories"]} multiple>
          <Accordion.Item value="categories">
            <Accordion.Control>
              {t("subClients.analytics.itemsByCategory")}
            </Accordion.Control>
            <Accordion.Panel>
              {analyticsPending ? (
                <div className="py-6 flex justify-center">
                  <Loader size="sm" />
                </div>
              ) : !analytics || analytics.totalItems === 0 ? (
                <EmptyState title={t("subClients.analytics.empty")} />
              ) : (
                <div className="w-full grid grid-cols-1 gap-3">
                  {analytics.categories.map((cat, idx) => {
                    const color = CARD_COLORS[idx % CARD_COLORS.length];
                    return (
                      <div
                        key={cat.productId}
                        className="border rounded-lg p-3 flex items-center gap-3"
                        style={{
                          borderColor: `var(--mantine-color-${color}-3)`,
                          backgroundColor: `var(--mantine-color-${color}-0)`,
                        }}
                      >
                        <div
                          className="text-xl font-extrabold min-w-[3rem] text-right"
                          style={{ color: `var(--mantine-color-${color}-7)` }}
                        >
                          {cat.count.toLocaleString()}
                        </div>
                        <IconCategory
                          size={14}
                          className="shrink-0"
                          style={{ color: `var(--mantine-color-${color}-7)` }}
                        />
                        <div className="text-xs font-semibold text-black break-words min-w-0">
                          {cat.productName}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </div>
    </div>
  );
};

export default AnalyticsTab;
